import type { Branding } from "@/features/manifest/model/types";

const DEFAULT_PRIMARY = "#0F766E";
const DEFAULT_SECONDARY = "#115E59";

/**
 * Convert "#0F766E" to "174 76% 26%" (HSL components for use inside hsl()).
 * Returns null on malformed input.
 */
export function hexToHslComponents(hex: string | null | undefined): string | null {
  if (hex == null || typeof hex !== "string") return null;

  let cleaned = hex.trim().replace("#", "");

  if (cleaned.length === 3) {
    cleaned = cleaned
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (cleaned.length !== 6) return null;
  if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return null;

  const r = parseInt(cleaned.slice(0, 2), 16) / 255;
  const g = parseInt(cleaned.slice(2, 4), 16) / 255;
  const b = parseInt(cleaned.slice(4, 6), 16) / 255;
  if ([r, g, b].some(Number.isNaN)) return null;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const BRANDING_CSS_VARS = [
  "--color-primary",
  "--color-ring",
  "--color-sidebar-primary",
  "--color-sidebar-ring",
  "--color-sidebar-accent",
  "--color-sidebar-accent-foreground",
  "--color-sidebar",
  "--color-sidebar-foreground",
  "--color-sidebar-border",
] as const;

/** Apply manifest branding colours to document CSS custom properties. */
export function applyBrandingCss(branding: Branding | undefined): void {
  const root = document.documentElement;

  if (!branding) {
    for (const prop of BRANDING_CSS_VARS) {
      root.style.removeProperty(prop);
    }
    root.style.removeProperty("--sidebar-muted");
    delete root.dataset.sidebarTone;
    return;
  }

  const primary = hexToHslComponents(branding.primary_color ?? DEFAULT_PRIMARY);
  if (primary) {
    const hsl = `hsl(${primary})`;
    root.style.setProperty("--color-primary", hsl);
    root.style.setProperty("--color-ring", hsl);
    root.style.setProperty("--color-sidebar-primary", hsl);
    root.style.setProperty("--color-sidebar-ring", hsl);
  }

  const secondary = hexToHslComponents(branding.secondary_color ?? DEFAULT_SECONDARY);
  if (secondary) {
    root.style.setProperty("--color-sidebar-accent", `hsl(${secondary} / 0.15)`);
    root.style.setProperty("--color-sidebar-accent-foreground", `hsl(${secondary})`);
  }

  if (branding.sidebar_bg) {
    const sidebarBg = hexToHslComponents(branding.sidebar_bg);
    if (sidebarBg) {
      root.style.setProperty("--color-sidebar", `hsl(${sidebarBg})`);
    }
  } else {
    root.style.removeProperty("--color-sidebar");
  }

  if (branding.sidebar_text) {
    const sidebarText = hexToHslComponents(branding.sidebar_text);
    if (sidebarText) {
      root.style.setProperty("--color-sidebar-foreground", `hsl(${sidebarText})`);
    }
  } else {
    root.style.removeProperty("--color-sidebar-foreground");
  }

  // A dark sidebar needs its own lines and highlight. The defaults are tuned
  // for a light sidebar: on a dark one the dividers glare white and the
  // highlighted item's dark text all but disappears. "Dark" is judged from
  // the sidebar background's lightness, not the theme name, so any dark
  // sidebar gets the same treatment. Derived values use the see-through
  // (alpha) form so they still work if the sidebar shade changes.
  const bgParts = branding.sidebar_bg ? hexToHslComponents(branding.sidebar_bg) : null;
  const textParts = branding.sidebar_text ? hexToHslComponents(branding.sidebar_text) : null;
  const lightness = bgParts ? Number(bgParts.split(" ")[2]?.replace("%", "")) : NaN;
  if (Number.isFinite(lightness) && lightness < 35 && textParts) {
    root.style.setProperty("--color-sidebar-border", `hsl(${textParts} / 0.12)`);
    root.style.setProperty("--color-sidebar-accent", `hsl(${textParts} / 0.1)`);
    root.style.setProperty("--color-sidebar-accent-foreground", `hsl(${textParts})`);
    root.style.setProperty("--sidebar-muted", `hsl(${textParts} / 0.66)`);
    root.dataset.sidebarTone = "dark";
  } else {
    // Light sidebar: the accent pair is already reset from the second
    // colour above; just drop the dark-only extras.
    root.style.removeProperty("--color-sidebar-border");
    root.style.removeProperty("--sidebar-muted");
    delete root.dataset.sidebarTone;
  }
}

/** Remove runtime branding overrides (e.g. on logout). */
export function clearBrandingCss(): void {
  applyBrandingCss(undefined);
}
